/**
 * Rules for the companion app banner (docs/features/companion-app-banner/prd.md),
 * ported from Atlas `frontend/src/components/shell/companionAppBannerRules.ts`.
 *
 * One free companion app per local day, in catalog order, among the apps this
 * merchant has not yet acted on. "Set up now" and ✕ both retire an app (B4);
 * after acting the banner stays hidden for the rest of that day (B5); once every
 * catalog app is retired nothing renders, until a new app joins the catalog (B6).
 *
 * Memory is `localStorage`, per store, per browser (W5). Storage that cannot
 * be read hides the banner (B8): a ✕ that cannot stick would return on every page.
 */

export interface BannerState {
  /** App ids the merchant clicked or closed; never shown again. */
  done: string[];
  /** The app picked for `day` (local `YYYY-MM-DD`), so a day shows one app only. */
  last: { id: string; day: string } | null;
}

export const EMPTY_BANNER_STATE: BannerState = { done: [], last: null };

/** Store-scoped, per the `<purpose>_<storeId>` convention (as PinnedSection.vue). */
export function companionAppBannerKey(storeId: number): string {
  return `companion_app_banner_${storeId}`;
}

/**
 * The browser's local calendar date. Not `toISOString()`: that is the UTC date,
 * which would switch apps mid-morning for a merchant east of UTC.
 */
export function localDay(now: Date): string {
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

/**
 * Narrow a stored value field by field; anything unreadable is the empty state.
 * A hand-edited or legacy entry must not throw or forward junk ids.
 */
export function parseBannerState(raw: string | null): BannerState {
  if (!raw) {
    return EMPTY_BANNER_STATE;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY_BANNER_STATE;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return EMPTY_BANNER_STATE;
  }
  const { done, last } = parsed as { done?: unknown; last?: unknown };
  if (!Array.isArray(done)) {
    return EMPTY_BANNER_STATE;
  }
  const l = last as { id?: unknown; day?: unknown } | null | undefined;
  return {
    done: done.filter((id): id is string => typeof id === 'string'),
    last:
      l && typeof l.id === 'string' && typeof l.day === 'string' ? { id: l.id, day: l.day } : null,
  };
}

/**
 * Which app to show today, and the state to persist.
 *
 * Same day as the last pick → that app, or nothing if it was retired today.
 * New day → the first eligible app after the last pick in catalog order,
 * wrapping (the first eligible app when the last pick is unknown).
 */
export function pickBannerApp(
  catalogIds: readonly string[],
  state: BannerState,
  today: string,
): { appId: string | null; state: BannerState } {
  const eligible = catalogIds.filter((id) => !state.done.includes(id));
  if (eligible.length === 0) {
    return { appId: null, state };
  }

  const { last } = state;
  if (last?.day === today) {
    return { appId: eligible.includes(last.id) ? last.id : null, state };
  }

  const start = last ? catalogIds.indexOf(last.id) : -1;
  for (let step = 1; step <= catalogIds.length; step++) {
    const id = catalogIds[(start + step + catalogIds.length) % catalogIds.length];
    if (eligible.includes(id)) {
      return { appId: id, state: { ...state, last: { id, day: today } } };
    }
  }
  return { appId: null, state }; // unreachable: eligible is a non-empty subset of the catalog
}

/** The stored state, or `null` when storage cannot be read (B8). */
export function readBannerState(storeId: number): BannerState | null {
  try {
    return parseBannerState(localStorage.getItem(companionAppBannerKey(storeId)));
  } catch {
    return null;
  }
}

export function writeBannerState(storeId: number, state: BannerState): void {
  try {
    localStorage.setItem(companionAppBannerKey(storeId), JSON.stringify(state));
  } catch {
    // The pick or retirement is lost for this browser; the banner may show again
    // next load. A promo is not worth anything louder.
  }
}

/**
 * Record today's pick. Re-reads storage first so a retirement another tab made
 * since this one read it is kept, not overwritten.
 */
export function recordPick(storeId: number, last: NonNullable<BannerState['last']>): void {
  const current = readBannerState(storeId);
  if (current) {
    writeBannerState(storeId, { ...current, last });
  }
}

/**
 * Retire an app, re-reading storage first (as `recordPick` does). The pick is
 * stamped with `today`: a tab left open since yesterday still holds yesterday's
 * pick, and without the stamp a reload after ✕ would show the next app the same
 * day (B5).
 */
export function retireApp(storeId: number, appId: string, today: string): void {
  const current = readBannerState(storeId) ?? EMPTY_BANNER_STATE;
  const done = current.done.includes(appId) ? current.done : [...current.done, appId];
  writeBannerState(storeId, { done, last: { id: appId, day: today } });
}

/**
 * Every `/assistant` route is an Atlas page, and Atlas draws its own banner
 * there (rev 2, W1/W6): one banner per page, owned by the app that draws it.
 */
export function isBannerSuppressedPath(pathname: string | null): boolean {
  if (!pathname) {
    return false;
  }
  return pathname === '/assistant' || pathname.startsWith('/assistant/');
}

const DEMO_SHOP = 'assisty-demo.myshopify.com';
const DEMO_HOST = 'demo.assisty.ai';

/** The shared demo store shows no promo (W6); same test as AppBanner.vue's `isDemoStore`. */
export function isDemoStore(shopName: string | undefined, hostname: string): boolean {
  return shopName === DEMO_SHOP || hostname === DEMO_HOST;
}
